/**
 * CanvasPreview Component
 *
 * Center panel that shows one or more canvases (count = product.printCount).
 * Each canvas has its own layout and photo drop targets. User navigates between
 * canvases via tabs. Slots are positioned using the layout's percentage geometry.
 */

import { PhotoSlot } from './PhotoSlot';
import { getLayoutById, getAspectRatioForLayout, SLOT_GAP_PERCENT } from '../types/canvas-layouts';
import type { EditPhotoMinimal } from '../types/edit-session';

export interface CanvasState {
  canvasIndex: number;
  layoutId: string;
  photoArrangement: Map<number, string>;
}

export interface CanvasPreviewProps {
  /** Number of canvases (product.printCount) */
  canvasCount: number;
  /** Current canvas index (0-based) */
  currentCanvasIndex: number;
  /** Layout id selected for the current canvas */
  currentLayoutId: string | null;
  /** Global slot index offset for the current canvas (sum of slot counts of previous canvases) */
  slotOffsetForCurrentCanvas: number;
  /** Combined photo arrangement: global slotIndex -> photoId */
  photoArrangement: Map<number, string>;
  /** All available photos */
  photos: EditPhotoMinimal[];
  /** Callback when user selects a different canvas */
  onCanvasChange: (canvasIndex: number) => void;
  /** Callback when a photo is dropped into a slot (global slotIndex) */
  onPhotoDropped: (slotIndex: number, photoId: string) => void;
  /** Optional callback when drop completes */
  onDropComplete?: (slotIndex: number, photoId: string, replaced: boolean) => void;
  /** Per-canvas: true if all photo slots for that canvas are filled (shows check on tab) */
  canvasSlotsFilled?: boolean[];
}

function getPhotoById(photos: EditPhotoMinimal[], photoId: string | null): EditPhotoMinimal | null {
  if (!photoId) return null;
  return photos.find((p) => p.photoId === photoId) || null;
}

/**
 * CanvasPreview - Center panel with canvas tabs and layout-based photo slots
 */
export function CanvasPreview({
  canvasCount,
  currentCanvasIndex,
  currentLayoutId,
  slotOffsetForCurrentCanvas,
  photoArrangement,
  photos,
  onCanvasChange,
  onPhotoDropped,
  onDropComplete,
  canvasSlotsFilled = [],
}: CanvasPreviewProps) {
  const layout = currentLayoutId ? getLayoutById(currentLayoutId) : null;

  if (!layout) {
    return (
      <div
        className="flex flex-col items-center justify-center h-full p-8"
        data-testid="canvas-preview-empty"
        role="status"
        aria-label="No layout selected"
      >
        <svg
          className="w-24 h-24 text-gray-300 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <p className="text-gray-500 text-lg text-center">
          Select a photo layout to arrange photos
        </p>
      </div>
    );
  }

  const slotCount = layout.slots.length;

  return (
    <div
      className="flex flex-col h-full"
      data-testid="canvas-preview"
      data-canvas-index={currentCanvasIndex}
    >
      <div className="mb-4 pb-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800" id="canvas-preview-title">
          Photo {currentCanvasIndex + 1}
        </h2>
        <p className="text-sm text-gray-600 mt-1" aria-live="polite">
          {layout.name} • {slotCount} photo{slotCount !== 1 ? 's' : ''} • {currentCanvasIndex + 1} of {canvasCount}
        </p>
      </div>

      {/* Canvas tabs when multiple canvases */}
      {canvasCount > 1 && (
        <div
          className="flex gap-2 mb-4"
          data-testid="canvas-tabs"
          role="tablist"
          aria-label="Photo navigation"
        >
          {Array.from({ length: canvasCount }, (_, i) => {
            const isSelected = i === currentCanvasIndex;
            const isFilled = canvasSlotsFilled[i] === true;
            return (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={isSelected}
                aria-controls={`canvas-panel-${i}`}
                id={`canvas-tab-${i}`}
                onClick={() => onCanvasChange(i)}
                className={`
                  relative w-10 h-10 rounded-lg font-medium transition-all duration-200
                  focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2
                  ${isSelected
                    ? 'bg-yellow-500 text-white shadow-md'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }
                `}
                data-testid={`canvas-tab-${i}`}
                aria-label={`Photo ${i + 1}${isFilled ? ', all slots filled' : ''}`}
              >
                {i + 1}
                {isFilled && (
                  <span
                    className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-green-500 text-white"
                    aria-hidden
                  >
                    <svg className="h-2.5 w-2.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Photo slots: paper fits in panel so all slots visible without scrolling */}
      <div
        id={`canvas-panel-${currentCanvasIndex}`}
        role="tabpanel"
        aria-labelledby={`canvas-tab-${currentCanvasIndex}`}
        className="flex-1 min-h-0 flex items-center justify-center p-2"
        data-testid="canvas-preview-slots"
      >
        {/* Paper: fit in panel while preserving aspect ratio (same as left-panel layout thumbnails) */}
        <div
          className="relative flex-shrink-0 bg-gray-50 rounded border border-gray-200"
          style={{
            aspectRatio: getAspectRatioForLayout(layout),
            height: '100%',
            width: 'auto',
            maxWidth: '100%',
            maxHeight: '100%',
          }}
        >
          {layout.slots.map((slot, localIndex) => {
            const globalSlotIndex = slotOffsetForCurrentCanvas + localIndex;
            const photoId = photoArrangement.get(globalSlotIndex) ?? null;
            const photo = getPhotoById(photos, photoId);
            const half = SLOT_GAP_PERCENT / 2;
            const left = slot.x + half;
            const top = slot.y + half;
            const width = Math.max(0, slot.width - SLOT_GAP_PERCENT);
            const height = Math.max(0, slot.height - SLOT_GAP_PERCENT);

            return (
              <div
                key={localIndex}
                className="absolute overflow-hidden rounded"
                style={{
                  left: `${left}%`,
                  top: `${top}%`,
                  width: `${width}%`,
                  height: `${height}%`,
                }}
              >
                <PhotoSlot
                  slotIndex={globalSlotIndex}
                  photoId={photoId}
                  photo={photo}
                  frame={null}
                  onPhotoDrop={(photoId) => onPhotoDropped(globalSlotIndex, photoId)}
                  onDropComplete={onDropComplete}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
